import { useState, useCallback, useMemo } from 'react'
import { Eye, Plus, TimerReset, X } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { Badge, Button, SegmentedControl } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { EmptyState } from '@/components/common/EmptyState'
import {
  TableRowAction,
  TableRowActions,
} from '@/components/common/TableRowActions'
import {
  DataTable,
  DataTableCard,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { getCompanyColor } from '@/features/shifts/utils/companyColor'
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter'
import { useTimeCorrectionsWorkspace } from '@/features/attendance/hooks/useTimeCorrection'
import { usePermissions } from '@/hooks/usePermissions'
import { formatDate, formatDateTime, formatRelative } from '@/lib/formatters'
import { CreateWorkspaceTimeCorrectionModal } from './CreateWorkspaceTimeCorrectionModal'
import { TimeCorrectionRequestDetailModal } from './TimeCorrectionRequestDetailModal'
import type {
  MyTimeCorrectionRequestListQuery,
  TimeCorrectionRequestStatus,
  TimeCorrectionRequest,
} from '@/features/attendance/types/timeCorrection.types'
import {
  TIME_CORRECTION_STATUS_VARIANT,
  getOriginalAttendance,
  getRequester,
  personDisplayName,
} from '@/features/attendance/utils/timeCorrectionDisplay'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const
type StatusFilter = 'all' | TimeCorrectionRequestStatus

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
] as const

const STATUS_LABEL: Record<TimeCorrectionRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
}

/** "In 10:00 PM · Out 3:59 AM" — the times the employee is asking for. */
function RequestedChange({
  request,
}: {
  request: TimeCorrectionRequest
}): React.ReactElement {
  const parts: React.ReactNode[] = []
  if (request.requestedClockIn) {
    parts.push(
      <span key="in">
        <span className="text-gray-500 dark:text-gray-400">In </span>
        {formatDate(request.requestedClockIn, 'p')}
      </span>
    )
  }
  if (request.requestedClockOut) {
    parts.push(
      <span key="out">
        <span className="text-gray-500 dark:text-gray-400">Out </span>
        {formatDate(request.requestedClockOut, 'p')}
      </span>
    )
  }
  if (request.requestedBreaks?.length) {
    const n = request.requestedBreaks.length
    parts.push(<span key="breaks">{n} break{n === 1 ? '' : 's'}</span>)
  }
  if (parts.length === 0) return <span className="text-gray-400">—</span>
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-0.5 tabular-nums text-gray-900 dark:text-gray-100">
      {parts}
    </span>
  )
}

export function WorkspaceTimeCorrectionsPage(): React.ReactElement {
  const { can } = usePermissions()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const filter = useWorkspaceCompanyFilter({ onChange: () => setPage(1) })
  const [createOpen, setCreateOpen] = useState(false)
  const [selected, setSelected] = useState<{
    companyId: string
    requestId: string
  } | null>(null)

  const query = useMemo<MyTimeCorrectionRequestListQuery>(
    () => ({
      page,
      limit,
      status: statusFilter === 'all' ? undefined : statusFilter,
      companyIds: filter.companyIds,
    }),
    [page, limit, statusFilter, filter.companyIds]
  )

  const { data, isLoading, error, refetch } = useTimeCorrectionsWorkspace(query)

  const requests = useMemo(() => data?.data ?? [], [data?.data])
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))
  // Anyone who can edit attendance directly fixes times from the attendance
  // record instead of filing a request for themselves to review.
  const canCreateTimeCorrection =
    can('time_corrections', 'create_self') && !can('attendance', 'update')
  const canReview = can('time_corrections', 'approve') || can('time_corrections', 'reject')
  const hasActiveFilters =
    filter.selectedCompanyIds.size > 0 || statusFilter !== 'all'

  const companyById = useMemo(() => {
    const map = new Map<string, { name: string; color?: string }>()
    for (const company of filter.companies) {
      map.set(company._id, { name: company.name, color: company.color })
    }
    return map
  }, [filter.companies])

  const selectedCompanyIds = Array.from(filter.selectedCompanyIds)
  const defaultCreateCompanyId =
    selectedCompanyIds.length === 1
      ? selectedCompanyIds[0]
      : filter.companies.length === 1
        ? filter.companies[0]._id
        : null

  const openRequest = useCallback((req: TimeCorrectionRequest) => {
    setSelected({ companyId: req.companyId, requestId: req._id })
  }, [])

  /** Pointer convenience; the row's action carries the same behaviour for keyboards. */
  const handleRowActivate = (req: TimeCorrectionRequest): void => {
    if (window.getSelection()?.toString()) return
    openRequest(req)
  }

  const handleResetFilters = (): void => {
    filter.reset()
    setStatusFilter('all')
    setPage(1)
  }

  /**
   * Everything a row shows, derived once so the table and the mobile card list
   * can never drift apart.
   */
  const describeRequest = (req: TimeCorrectionRequest) => {
    const requester = getRequester(req)
    const displayName = personDisplayName(requester)
    const email = requester?.email?.trim()
    const shiftDate = getOriginalAttendance(req)?.date
    return {
      displayName,
      // Secondary line only when it adds something the name doesn't already say.
      email: email && email !== displayName ? email : null,
      avatar: requester?.avatar,
      company: companyById.get(req.companyId),
      companyColor: getCompanyColor(req.companyId, companyById.get(req.companyId)?.color),
      shiftDate: shiftDate ? formatDate(shiftDate, 'EEE, d MMM') : '—',
      submitted: req.createdAt ? formatRelative(req.createdAt) : '—',
      submittedTitle: req.createdAt ? formatDateTime(req.createdAt) : undefined,
      needsReview: canReview && req.status === 'pending',
    }
  }

  const renderPerson = (
    row: ReturnType<typeof describeRequest>,
    size: 'md' | 'lg'
  ): React.ReactElement => (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={row.displayName} src={row.avatar} size={size} tone="soft" />
      <div className="min-w-0">
        <p className="truncate font-medium text-gray-900 dark:text-gray-100">
          {row.displayName}
        </p>
        {row.email ? (
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {row.email}
          </p>
        ) : null}
      </div>
    </div>
  )

  const renderCompany = (
    row: ReturnType<typeof describeRequest>
  ): React.ReactElement => (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: row.companyColor.dot }}
      />
      <span className="truncate text-gray-700 dark:text-gray-300">
        {row.company?.name ?? '—'}
      </span>
    </span>
  )

  const renderStatus = (
    req: TimeCorrectionRequest,
    size?: 'sm'
  ): React.ReactElement => (
    <Badge variant={TIME_CORRECTION_STATUS_VARIANT[req.status]} dot size={size}>
      {STATUS_LABEL[req.status]}
    </Badge>
  )

  /**
   * Pending requests the viewer can act on get a visible "Review" button — it's
   * the work this page exists for. Everything else is a quiet hover action.
   */
  const renderAction = (
    req: TimeCorrectionRequest,
    row: ReturnType<typeof describeRequest>
  ): React.ReactElement =>
    row.needsReview ? (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={(event) => {
          event.stopPropagation()
          openRequest(req)
        }}
        aria-label={`Review request from ${row.displayName}`}
      >
        Review
      </Button>
    ) : (
      <TableRowActions>
        <TableRowAction
          icon={Eye}
          label={`View request from ${row.displayName}`}
          onSelect={() => openRequest(req)}
        />
      </TableRowActions>
    )

  const renderTable = (): React.ReactElement => (
    <DataTable
      className="hidden md:block"
      tableClassName="w-full"
      caption="Time correction requests"
    >
      <DataTableHead>
        <tr>
          <DataTableHeadCell className="w-[26%] px-6 py-3">Employee</DataTableHeadCell>
          <DataTableHeadCell className="w-[15%] px-6 py-3">Company</DataTableHeadCell>
          <DataTableHeadCell className="w-[11%] px-6 py-3">Shift date</DataTableHeadCell>
          <DataTableHeadCell className="w-[18%] px-6 py-3">Requested change</DataTableHeadCell>
          <DataTableHeadCell className="w-[12%] px-6 py-3">Submitted</DataTableHeadCell>
          <DataTableHeadCell className="w-[9%] px-6 py-3">Status</DataTableHeadCell>
          <DataTableHeadCell align="right" className="w-[9%] px-6 py-3">
            <span className="sr-only">Actions</span>
          </DataTableHeadCell>
        </tr>
      </DataTableHead>
      <DataTableBody>
        {requests.map((req) => {
          const row = describeRequest(req)
          return (
            <DataTableRow
              key={req._id}
              className="group/row cursor-pointer"
              onClick={() => handleRowActivate(req)}
            >
              <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                {renderPerson(row, 'md')}
              </DataTableCell>
              <DataTableCell noWrap={false} className="px-6 py-3 align-middle">
                {renderCompany(row)}
              </DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">
                {row.shiftDate}
              </DataTableCell>
              <DataTableCell noWrap={false} className="px-6 py-3 align-middle">
                <RequestedChange request={req} />
              </DataTableCell>
              <DataTableCell
                className="px-6 py-3 align-middle text-gray-600 dark:text-gray-400"
                title={row.submittedTitle}
              >
                {row.submitted}
              </DataTableCell>
              <DataTableCell className="px-6 py-3 align-middle">
                {renderStatus(req)}
              </DataTableCell>
              <DataTableCell align="right" className="px-6 py-3 align-middle">
                {renderAction(req, row)}
              </DataTableCell>
            </DataTableRow>
          )
        })}
      </DataTableBody>
    </DataTable>
  )

  /** Phone layout: each request becomes a card with the same facts stacked. */
  const renderCards = (): React.ReactElement => (
    <ul className="flex flex-col gap-2 md:hidden">
      {requests.map((req) => {
        const row = describeRequest(req)
        return (
          <li
            key={req._id}
            className="group/row cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors active:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:active:bg-gray-700/40"
            onClick={() => handleRowActivate(req)}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                {renderPerson(row, 'lg')}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  {renderCompany(row)}
                  <span className="text-gray-500 dark:text-gray-400">
                    {row.shiftDate}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                {renderStatus(req, 'sm')}
                {renderAction(req, row)}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 text-sm dark:border-gray-700/60">
              <RequestedChange request={req} />
              <span
                className="shrink-0 text-xs text-gray-500 dark:text-gray-400"
                title={row.submittedTitle}
              >
                {row.submitted}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )

  return (
    <>
      <PageShell
        breadcrumbItems={[
          { label: 'Time & Attendance', to: '/dashboard' },
          { label: 'Time corrections' },
        ]}
        icon={TimerReset}
        title="Time correction requests"
        description="Cross-company time correction requests across every company you can read."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={<WorkspaceCompanyChips filter={filter} />}
            actions={
              canCreateTimeCorrection ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                  disabled={filter.companies.length === 0}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Request correction</span>
                </Button>
              ) : null
            }
          />

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Status
              </span>
              <SegmentedControl
                ariaLabel="Filter by status"
                size="sm"
                options={[...STATUS_FILTER_OPTIONS]}
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value as StatusFilter)
                  setPage(1)
                }}
              />
            </div>

            {hasActiveFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-gray-600 dark:text-gray-400"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                <span className="ml-1">Reset</span>
              </Button>
            ) : null}

            <p
              className="ml-auto text-xs text-gray-500 dark:text-gray-400"
              aria-live="polite"
            >
              {isLoading
                ? 'Loading requests…'
                : `${total} ${total === 1 ? 'request' : 'requests'}`}
            </p>
          </div>

          {error ? (
            <QueryErrorState
              error={error}
              title="Couldn't load time correction requests"
              onRetry={refetch}
            />
          ) : isLoading ? (
            <DataTableCard>
              <TableSkeleton rows={limit > 10 ? 10 : limit} cols={7} />
            </DataTableCard>
          ) : requests.length === 0 ? (
            <EmptyState
              icon={TimerReset}
              title={
                hasActiveFilters
                  ? 'No requests match these filters'
                  : 'No time correction requests'
              }
              description={
                hasActiveFilters
                  ? 'Try a different company or status.'
                  : 'Requests appear here when staff ask to correct a clock-in or clock-out.'
              }
              action={
                hasActiveFilters ? (
                  <Button variant="secondary" size="sm" onClick={handleResetFilters}>
                    Reset filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              {renderCards()}
              {renderTable()}

              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onLimitChange={(next) => {
                  setLimit(next)
                  setPage(1)
                }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                className="px-6 py-3"
              />
            </>
          )}
        </div>
      </PageShell>

      <TimeCorrectionRequestDetailModal
        companyId={selected?.companyId ?? ''}
        requestId={selected?.requestId ?? null}
        companyName={
          selected ? filter.companyNameById.get(selected.companyId) : undefined
        }
        onClose={() => setSelected(null)}
        onActionComplete={() => setSelected(null)}
      />

      <CreateWorkspaceTimeCorrectionModal
        open={createOpen}
        companies={filter.companies}
        defaultCompanyId={defaultCreateCompanyId}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false)
          void refetch()
        }}
      />
    </>
  )
}

WorkspaceTimeCorrectionsPage.displayName = 'WorkspaceTimeCorrectionsPage'
